import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import {
  Save, Plus, Trash2, Target, ChevronDown, ChevronUp, Sparkles,
  BarChart3, Calendar, CheckCircle2, AlertTriangle, TrendingUp,
  TrendingDown, Minus, XCircle, HelpCircle, Settings2,
  Loader2, Edit3, Info
} from 'lucide-react';
import axios from 'axios';

import { API_URL } from '../../utils/api';

const DEFAULT_AREAS = ['Language Arts', 'Math', 'Communication', 'Social/Emotional', 'Behavior', 'Motor Skills', 'Daily Living', 'Other'];

const PROGRESS_STATUSES = [
  { value: 'mastered', label: 'Mastered', icon: CheckCircle2, color: 'text-green-600 bg-green-50 border-green-200' },
  { value: 'sufficient_progress', label: 'Sufficient Progress', icon: TrendingUp, color: 'text-blue-600 bg-blue-50 border-blue-200' },
  { value: 'progressing', label: 'Progressing', icon: TrendingUp, color: 'text-sky-600 bg-sky-50 border-sky-200' },
  { value: 'insufficient_progress', label: 'Insufficient Progress', icon: AlertTriangle, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  { value: 'no_progress', label: 'No Progress', icon: Minus, color: 'text-gray-500 bg-gray-50 border-gray-200' },
  { value: 'regression', label: 'Regression', icon: TrendingDown, color: 'text-red-600 bg-red-50 border-red-200' },
  { value: 'not_reported', label: 'Not Reported', icon: HelpCircle, color: 'text-gray-400 bg-gray-50 border-gray-200' },
];

const SCHEDULE_PRESETS = [
  { value: 'quarterly', label: 'Quarterly (4 periods)' },
  { value: 'trimester', label: 'Trimester (3 periods)' },
  { value: 'semester', label: 'Semester (2 periods)' },
  { value: 'custom', label: 'Custom' },
];

function getStatusConfig(status) {
  return PROGRESS_STATUSES.find(s => s.value === status) || PROGRESS_STATUSES[6];
}

function mergeContent(existing) {
  const defaults = { goal_areas: [] };
  if (!existing || typeof existing !== 'object') return defaults;
  if (existing.notes && !existing.goal_areas) return defaults;
  return { ...defaults, ...existing };
}

export default function GoalsEditor({ section, caseData, onSave, saving }) {
  const [content, setContent] = useState(() => mergeContent(section?.content));
  const [collapsed, setCollapsed] = useState({});
  const [progressOpen, setProgressOpen] = useState({});
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [schedule, setSchedule] = useState(caseData?.reporting_schedule || null);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  // AI suggestion state
  const [aiOpen, setAiOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [suggestedAreas, setSuggestedAreas] = useState(null);
  const [aiMeta, setAiMeta] = useState(null);
  const [suggestionStates, setSuggestionStates] = useState({});

  useEffect(() => {
    setContent(mergeContent(section?.content));
  }, [section?.section_id]);

  useEffect(() => {
    setSchedule(caseData?.reporting_schedule || null);
  }, [caseData?.case_id]);

  // --- Schedule Management ---
  const applyPreset = async (presetType) => {
    try {
      const res = await axios.get(`${API_URL}/api/iep-met/reporting-schedule-presets`, { withCredentials: true });
      const preset = res.data.presets[presetType];
      if (preset) setSchedule(preset);
    } catch (err) {
      console.error('Failed to load preset', err);
    }
  };

  const saveSchedule = async () => {
    if (!schedule || !caseData?.case_id) return;
    setScheduleSaving(true);
    try {
      await axios.put(`${API_URL}/api/iep-met/cases/${caseData.case_id}/reporting-schedule`,
        { reporting_schedule: schedule }, { withCredentials: true });
      setScheduleOpen(false);
    } catch (err) {
      console.error('Failed to save schedule', err);
    } finally {
      setScheduleSaving(false);
    }
  };

  const updatePeriod = (idx, field, value) => {
    setSchedule(prev => ({
      ...prev,
      periods: prev.periods.map((p, i) => i === idx ? { ...p, [field]: value } : p),
    }));
  };

  const addPeriod = () => {
    setSchedule(prev => ({
      ...prev,
      schedule_type: 'custom',
      periods: [...(prev?.periods || []), { period_number: (prev?.periods?.length || 0) + 1, label: `Period ${(prev?.periods?.length || 0) + 1}`, start_date: '', end_date: '' }],
    }));
  };

  const removePeriod = (idx) => {
    setSchedule(prev => ({
      ...prev,
      schedule_type: 'custom',
      periods: prev.periods.filter((_, i) => i !== idx).map((p, i) => ({ ...p, period_number: i + 1 })),
    }));
  };

  // --- AI Goal Suggestion Workflow ---
  const generateGoals = async () => {
    setGenerating(true);
    setSuggestedAreas(null);
    setAiMeta(null);
    setSuggestionStates({});
    try {
      const res = await axios.post(
        `${API_URL}/api/iep-met/cases/${caseData.case_id}/sections/goals/draft`,
        {}, { withCredentials: true }
      );
      const output = res.data?.output;
      if (output?.suggested_areas) {
        setSuggestedAreas(output.suggested_areas);
        setAiMeta({
          dataSources: output.data_sources_used || [],
          notes: output.notes || '',
          gaps: output.gaps_identified || [],
          model: res.data?.model_used,
          inputSnapshot: res.data?.input_snapshot,
        });
        // Initialize states: key = "areaIdx-goalIdx"
        const initial = {};
        output.suggested_areas.forEach((area, ai) => {
          area.goals?.forEach((_, gi) => { initial[`${ai}-${gi}`] = 'pending'; });
        });
        setSuggestionStates(initial);
      }
    } catch (err) {
      console.error('AI goal generation failed', err);
      setAiMeta({ error: err.response?.data?.detail || 'AI generation failed. Please try again.' });
    } finally {
      setGenerating(false);
    }
  };

  const acceptGoal = (areaIdx, goalIdx) => {
    const area = suggestedAreas[areaIdx];
    const goal = area.goals[goalIdx];
    setContent(prev => {
      const existing = prev.goal_areas.find(a => a.area_name === area.area_name);
      if (existing) {
        return {
          ...prev,
          goal_areas: prev.goal_areas.map(a =>
            a.area_name === area.area_name ? {
              ...a,
              goals: [...a.goals, {
                goal_id: `goal_${Date.now()}_${goalIdx}`, goal_text: goal.goal_text,
                baseline: goal.baseline || '', target_date: '', measurement_method: goal.measurement_method || '',
                progress_notes: '', progress_reports: [], source: 'ai_suggested',
                justification: goal.justification, confidence: goal.confidence, data_basis: goal.data_basis,
              }],
            } : a
          ),
        };
      }
      return {
        ...prev,
        goal_areas: [...prev.goal_areas, {
          area_id: `area_${Date.now()}_${areaIdx}`, area_name: area.area_name,
          goals: [{
            goal_id: `goal_${Date.now()}_${goalIdx}`, goal_text: goal.goal_text,
            baseline: goal.baseline || '', target_date: '', measurement_method: goal.measurement_method || '',
            progress_notes: '', progress_reports: [], source: 'ai_suggested',
            justification: goal.justification, confidence: goal.confidence, data_basis: goal.data_basis,
          }],
        }],
      };
    });
    setSuggestionStates(prev => ({ ...prev, [`${areaIdx}-${goalIdx}`]: 'accepted' }));
  };

  const editGoal = (areaIdx, goalIdx) => {
    // Same as accept but marks as ai_edited so user knows to modify it
    const area = suggestedAreas[areaIdx];
    const goal = area.goals[goalIdx];
    setContent(prev => {
      const existing = prev.goal_areas.find(a => a.area_name === area.area_name);
      const newGoal = {
        goal_id: `goal_${Date.now()}_${goalIdx}`, goal_text: goal.goal_text,
        baseline: goal.baseline || '', target_date: '', measurement_method: goal.measurement_method || '',
        progress_notes: '', progress_reports: [], source: 'ai_edited',
        justification: goal.justification, confidence: goal.confidence, data_basis: goal.data_basis,
      };
      if (existing) {
        return { ...prev, goal_areas: prev.goal_areas.map(a => a.area_name === area.area_name ? { ...a, goals: [...a.goals, newGoal] } : a) };
      }
      return { ...prev, goal_areas: [...prev.goal_areas, { area_id: `area_${Date.now()}_${areaIdx}`, area_name: area.area_name, goals: [newGoal] }] };
    });
    setSuggestionStates(prev => ({ ...prev, [`${areaIdx}-${goalIdx}`]: 'edited' }));
  };

  const rejectGoal = (areaIdx, goalIdx) => {
    setSuggestionStates(prev => ({ ...prev, [`${areaIdx}-${goalIdx}`]: 'rejected' }));
  };

  const acceptAllPending = () => {
    suggestedAreas.forEach((area, ai) => {
      area.goals?.forEach((_, gi) => {
        if (suggestionStates[`${ai}-${gi}`] === 'pending') acceptGoal(ai, gi);
      });
    });
  };

  const pendingCount = suggestedAreas ? Object.values(suggestionStates).filter(s => s === 'pending').length : 0;

  // --- Goal Area Management ---
  const addArea = (areaName) => {
    if (!areaName) return;
    setContent(prev => ({
      ...prev,
      goal_areas: [...prev.goal_areas, { area_id: `area_${Date.now()}`, area_name: areaName, goals: [] }],
    }));
  };

  const removeArea = (areaIdx) => {
    setContent(prev => ({ ...prev, goal_areas: prev.goal_areas.filter((_, i) => i !== areaIdx) }));
  };

  const addGoal = (areaIdx) => {
    setContent(prev => ({
      ...prev,
      goal_areas: prev.goal_areas.map((area, i) =>
        i === areaIdx ? {
          ...area,
          goals: [...area.goals, {
            goal_id: `goal_${Date.now()}`, goal_text: '', baseline: '', target_date: '',
            measurement_method: '', progress_notes: '', progress_reports: [],
          }],
        } : area
      ),
    }));
  };

  const removeGoal = (areaIdx, goalIdx) => {
    setContent(prev => ({
      ...prev,
      goal_areas: prev.goal_areas.map((area, i) =>
        i === areaIdx ? { ...area, goals: area.goals.filter((_, j) => j !== goalIdx) } : area
      ),
    }));
  };

  const updateGoal = (areaIdx, goalIdx, field, value) => {
    setContent(prev => ({
      ...prev,
      goal_areas: prev.goal_areas.map((area, i) =>
        i === areaIdx ? {
          ...area,
          goals: area.goals.map((g, j) => j === goalIdx ? { ...g, [field]: value } : g),
        } : area
      ),
    }));
  };

  // --- Progress Report Management ---
  const getOrCreateReports = useCallback((goal) => {
    if (!schedule?.periods) return goal.progress_reports || [];
    const existing = goal.progress_reports || [];
    return schedule.periods.map(p => {
      const found = existing.find(r => r.period_number === p.period_number);
      return found || {
        period_number: p.period_number,
        period_label: p.label,
        status: 'not_reported',
        current_performance: '',
        narrative: '',
        reported_date: '',
        reported_by: '',
        data_source: 'manual',
      };
    });
  }, [schedule]);

  const updateProgressReport = (areaIdx, goalIdx, periodNum, field, value) => {
    setContent(prev => ({
      ...prev,
      goal_areas: prev.goal_areas.map((area, ai) =>
        ai === areaIdx ? {
          ...area,
          goals: area.goals.map((g, gi) => {
            if (gi !== goalIdx) return g;
            const reports = getOrCreateReports(g);
            return {
              ...g,
              progress_reports: reports.map(r =>
                r.period_number === periodNum ? { ...r, [field]: value } : r
              ),
            };
          }),
        } : area
      ),
    }));
  };

  const handleSave = () => onSave(content);
  const existingAreaNames = new Set(content.goal_areas.map(a => a.area_name));
  const availableAreas = DEFAULT_AREAS.filter(a => !existingAreaNames.has(a));
  const totalGoals = content.goal_areas.reduce((sum, a) => sum + a.goals.length, 0);
  const periods = schedule?.periods || [];

  return (
    <div className="max-w-4xl space-y-6" data-testid="goals-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]" data-testid="goals-title">Goals & Objectives</h2>
          <p className="text-sm text-[#595959] mt-1">{section?.description}</p>
          <div className="flex items-center gap-2 mt-2">
            <Badge className="bg-[#3B6A9B]/10 text-[#3B6A9B] text-xs">{content.goal_areas.length} areas, {totalGoals} goals</Badge>
            {schedule && <Badge className="bg-green-50 text-green-700 text-xs border border-green-200"><BarChart3 className="h-3 w-3 mr-1" />{schedule.schedule_type} reporting</Badge>}
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl shrink-0" data-testid="goals-save-top">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {/* AI Goal Suggestion Panel */}
      <div className="bg-gradient-to-r from-[#3B6A9B]/5 to-[#5A8A5A]/5 border border-[#3B6A9B]/20 rounded-2xl overflow-hidden" data-testid="ai-goals-panel">
        <button className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-white/40 transition-colors" onClick={() => setAiOpen(!aiOpen)} data-testid="ai-goals-toggle">
          <div className="flex items-center gap-2.5">
            <Sparkles className="h-4 w-4 text-[#3B6A9B]" />
            <span className="text-sm font-medium text-[#1C1C1C]">AI Goal Suggestions</span>
            <Badge className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200">Advisory Only</Badge>
            {suggestedAreas && <Badge className="text-[10px] bg-[#3B6A9B]/10 text-[#3B6A9B]">{suggestedAreas.reduce((s, a) => s + (a.goals?.length || 0), 0)} suggestions</Badge>}
          </div>
          {aiOpen ? <ChevronUp className="h-4 w-4 text-[#595959]" /> : <ChevronDown className="h-4 w-4 text-[#595959]" />}
        </button>

        {aiOpen && (
          <div className="px-5 pb-5 border-t border-[#3B6A9B]/10">
            <div className="flex items-center justify-between mt-3 mb-4">
              <p className="text-xs text-[#595959] max-w-lg">
                AI analyzes present levels, evaluation findings, disability impact, and accommodations to suggest measurable annual goals. All suggestions require team review.
              </p>
              <Button onClick={generateGoals} disabled={generating}
                className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl text-xs h-8 shrink-0" data-testid="generate-goals-btn">
                {generating ? <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" />Analyzing...</>
                  : <><Sparkles className="h-3 w-3 mr-1.5" />{suggestedAreas ? 'Regenerate' : 'Generate Goals'}</>}
              </Button>
            </div>

            {aiMeta?.error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-start gap-2" data-testid="ai-goals-error">
                <AlertTriangle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                <p className="text-xs text-red-700">{aiMeta.error}</p>
              </div>
            )}

            {suggestedAreas && suggestedAreas.length > 0 && (
              <>
                {aiMeta && !aiMeta.error && (
                  <div className="bg-white/60 border border-[#E5E5E5] rounded-xl p-3 mb-4" data-testid="ai-goals-meta">
                    <div className="flex items-start gap-4 text-xs text-[#595959]">
                      <div className="flex-1">
                        {aiMeta.notes && <p className="mb-1"><strong>AI Notes:</strong> {aiMeta.notes}</p>}
                        {aiMeta.dataSources?.length > 0 && <p className="mb-1"><strong>Data used:</strong> {aiMeta.dataSources.join(', ')}</p>}
                        {aiMeta.gaps?.length > 0 && <p className="text-amber-600"><strong>Data gaps:</strong> {aiMeta.gaps.join('; ')}</p>}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-[10px] text-[#595959]">{pendingCount} pending</span>
                        {pendingCount > 0 && (
                          <Button variant="outline" size="sm" className="rounded-lg text-xs h-7 border-green-300 text-green-700 hover:bg-green-50" onClick={acceptAllPending} data-testid="accept-all-goals-btn">
                            <CheckCircle2 className="h-3 w-3 mr-1" />Accept All
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-4" data-testid="ai-goal-areas">
                  {suggestedAreas.map((area, areaIdx) => (
                    <div key={areaIdx} className="border border-[#E5E5E5] rounded-xl overflow-hidden bg-white" data-testid={`ai-area-${areaIdx}`}>
                      <div className="px-4 py-2.5 bg-[#F3F4F1] flex items-center gap-2">
                        <Target className="h-3.5 w-3.5 text-[#3B6A9B]" />
                        <span className="text-xs font-semibold text-[#1C1C1C]">{area.area_name}</span>
                        <span className="text-[10px] text-[#595959]">({area.goals?.length || 0} goals)</span>
                      </div>
                      {area.rationale && <p className="px-4 py-2 text-[10px] text-[#595959] border-b border-[#E5E5E5] bg-[#F3F4F1]/50">{area.rationale}</p>}
                      <div className="p-3 space-y-2">
                        {(area.goals || []).map((goal, goalIdx) => {
                          const key = `${areaIdx}-${goalIdx}`;
                          const state = suggestionStates[key] || 'pending';
                          const isPending = state === 'pending';
                          return (
                            <div key={goalIdx} className={`border rounded-xl p-3 transition-all ${
                              state === 'accepted' ? 'bg-green-50/50 border-green-200 opacity-75' :
                              state === 'rejected' ? 'bg-gray-50 border-gray-200 opacity-40' :
                              state === 'edited' ? 'bg-blue-50/50 border-blue-200 opacity-75' :
                              'border-[#E5E5E5]'
                            }`} data-testid={`ai-goal-${areaIdx}-${goalIdx}`}>
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                    {goal.confidence && <Badge className={`text-[10px] border ${
                                      goal.confidence === 'high' ? 'bg-green-50 text-green-700 border-green-200' :
                                      goal.confidence === 'medium' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                      'bg-gray-50 text-gray-500 border-gray-200'
                                    }`}>{goal.confidence}</Badge>}
                                    {!isPending && (
                                      <Badge className={`text-[10px] ${
                                        state === 'accepted' ? 'bg-green-100 text-green-700' : state === 'edited' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                                      }`}>{state === 'accepted' ? 'Accepted' : state === 'edited' ? 'Accepted (to edit)' : 'Rejected'}</Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-[#1C1C1C] mb-2">{goal.goal_text}</p>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[10px] text-[#595959]">
                                    {goal.baseline && <p><strong>Baseline:</strong> {goal.baseline}</p>}
                                    {goal.target && <p><strong>Target:</strong> {goal.target}</p>}
                                    {goal.measurement_method && <p><strong>Measurement:</strong> {goal.measurement_method}</p>}
                                    {goal.data_basis && <p><strong>Data basis:</strong> {goal.data_basis}</p>}
                                  </div>
                                  {goal.justification && <p className="text-[10px] text-[#595959] mt-1 italic">{goal.justification}</p>}
                                </div>
                                {isPending && (
                                  <div className="flex items-center gap-1 shrink-0">
                                    <Button variant="ghost" size="sm" onClick={() => acceptGoal(areaIdx, goalIdx)}
                                      className="h-7 w-7 p-0 text-green-600 hover:bg-green-50 rounded-lg" data-testid={`accept-goal-${areaIdx}-${goalIdx}`}>
                                      <CheckCircle2 className="h-4 w-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => editGoal(areaIdx, goalIdx)}
                                      className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50 rounded-lg" data-testid={`edit-goal-${areaIdx}-${goalIdx}`}>
                                      <Edit3 className="h-4 w-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => rejectGoal(areaIdx, goalIdx)}
                                      className="h-7 w-7 p-0 text-red-400 hover:bg-red-50 rounded-lg" data-testid={`reject-goal-${areaIdx}-${goalIdx}`}>
                                      <XCircle className="h-4 w-4" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {aiMeta?.inputSnapshot && (
                  <div className="mt-3 text-[10px] text-[#595959]/60 flex items-center gap-2">
                    <Info className="h-3 w-3" />
                    <span>Context: {aiMeta.inputSnapshot.context_type} IEP | Sources: {aiMeta.inputSnapshot.data_sources?.join(', ') || 'none'} | Intensity: {aiMeta.inputSnapshot.ai_intensity}</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Reporting Schedule Setup */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden" data-testid="reporting-schedule-section">
        <button className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-[#F3F4F1] transition-colors"
          onClick={() => setScheduleOpen(!scheduleOpen)} data-testid="schedule-toggle">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-[#3B6A9B]" />
            <span className="text-sm font-medium text-[#1C1C1C]">Reporting Schedule</span>
            {schedule ? (
              <Badge className="bg-green-50 text-green-700 text-xs border border-green-200">
                {schedule.schedule_type} — {periods.length} periods
              </Badge>
            ) : (
              <Badge className="bg-gray-100 text-gray-500 text-xs">Not configured</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Settings2 className="h-3.5 w-3.5 text-[#595959]" />
            {scheduleOpen ? <ChevronUp className="h-4 w-4 text-[#595959]" /> : <ChevronDown className="h-4 w-4 text-[#595959]" />}
          </div>
        </button>
        {scheduleOpen && (
          <div className="px-5 pb-5 border-t border-[#E5E5E5]">
            <p className="text-xs text-[#595959] mt-3 mb-3">
              Set the reporting schedule for progress reports. Each goal will have a progress entry for every period.
            </p>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs text-[#595959]">Apply preset:</span>
              {SCHEDULE_PRESETS.filter(p => p.value !== 'custom').map(preset => (
                <Button key={preset.value} variant="outline" size="sm"
                  className={`rounded-lg text-xs h-7 ${schedule?.schedule_type === preset.value ? 'border-[#3B6A9B] text-[#3B6A9B] bg-[#3B6A9B]/5' : ''}`}
                  onClick={() => applyPreset(preset.value)} data-testid={`preset-${preset.value}`}>
                  {preset.label}
                </Button>
              ))}
            </div>
            {schedule && (
              <>
                <div className="space-y-2 mb-3">
                  {periods.map((p, idx) => (
                    <div key={idx} className="flex items-center gap-3 border border-[#E5E5E5] rounded-xl p-2.5" data-testid={`period-row-${idx}`}>
                      <Input value={p.label} onChange={e => updatePeriod(idx, 'label', e.target.value)}
                        className="rounded-lg border-[#E5E5E5] text-xs h-8 w-28" placeholder="Label" />
                      <div className="flex items-center gap-1.5 flex-1">
                        <Input type="date" value={p.start_date} onChange={e => updatePeriod(idx, 'start_date', e.target.value)}
                          className="rounded-lg border-[#E5E5E5] text-xs h-8" data-testid={`period-start-${idx}`} />
                        <span className="text-xs text-[#595959]">to</span>
                        <Input type="date" value={p.end_date} onChange={e => updatePeriod(idx, 'end_date', e.target.value)}
                          className="rounded-lg border-[#E5E5E5] text-xs h-8" data-testid={`period-end-${idx}`} />
                      </div>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        onClick={() => removePeriod(idx)} data-testid={`remove-period-${idx}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <Button variant="outline" size="sm" className="rounded-lg text-xs h-7" onClick={addPeriod} data-testid="add-period-btn">
                    <Plus className="h-3 w-3 mr-1" />Add Period
                  </Button>
                  <Button size="sm" className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-lg text-xs h-7"
                    onClick={saveSchedule} disabled={scheduleSaving} data-testid="save-schedule-btn">
                    <Save className="h-3 w-3 mr-1" />{scheduleSaving ? 'Saving...' : 'Save Schedule'}
                  </Button>
                </div>
              </>
            )}
            {!schedule && (
              <p className="text-xs text-[#595959] italic text-center py-4">Select a preset above to configure the reporting schedule.</p>
            )}
          </div>
        )}
      </div>

      {/* Add Goal Area */}
      {availableAreas.length > 0 && (
        <div className="flex items-center gap-3">
          <span className="text-sm text-[#595959]">Add goal area:</span>
          <div className="flex gap-2 flex-wrap">
            {availableAreas.map(area => (
              <Button key={area} variant="outline" size="sm" className="rounded-lg text-xs h-7" onClick={() => addArea(area)} data-testid={`add-area-${area}`}>
                <Plus className="h-3 w-3 mr-1" />{area}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Goal Areas */}
      {content.goal_areas.length === 0 ? (
        <div className="bg-white border border-[#E5E5E5] rounded-2xl p-8 text-center">
          <Target className="h-8 w-8 text-[#E5E5E5] mx-auto mb-2" />
          <p className="text-sm text-[#595959]">No goal areas added yet. Use the buttons above to add a goal area.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {content.goal_areas.map((area, areaIdx) => {
            const isCollapsed = collapsed[area.area_id];
            return (
              <div key={area.area_id} className="bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden" data-testid={`goal-area-${areaIdx}`}>
                <div className="flex items-center justify-between px-5 py-3.5 bg-[#F3F4F1] cursor-pointer" onClick={() => setCollapsed(prev => ({ ...prev, [area.area_id]: !prev[area.area_id] }))}>
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-[#3B6A9B]" />
                    <h3 className="font-medium text-sm text-[#1C1C1C]">{area.area_name}</h3>
                    <span className="text-xs text-[#595959]">({area.goals.length} goals)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50" onClick={e => { e.stopPropagation(); removeArea(areaIdx); }} data-testid={`remove-area-${areaIdx}`}>
                      <Trash2 className="h-3 w-3 mr-1" />Remove
                    </Button>
                    {isCollapsed ? <ChevronDown className="h-4 w-4 text-[#595959]" /> : <ChevronUp className="h-4 w-4 text-[#595959]" />}
                  </div>
                </div>
                {!isCollapsed && (
                  <div className="p-5">
                    {area.goals.length === 0 ? (
                      <p className="text-sm text-[#595959] text-center py-2">No goals in this area yet.</p>
                    ) : (
                      <div className="space-y-4">
                        {area.goals.map((goal, goalIdx) => {
                          const reports = getOrCreateReports(goal);
                          const reportedCount = reports.filter(r => r.status && r.status !== 'not_reported').length;
                          const isProgressOpen = progressOpen[goal.goal_id];

                          return (
                            <div key={goal.goal_id} className="border border-[#E5E5E5] rounded-xl overflow-hidden group" data-testid={`goal-${areaIdx}-${goalIdx}`}>
                              <div className="p-4 relative">
                                <button onClick={() => removeGoal(areaIdx, goalIdx)} className="absolute top-3 right-3 p-1 rounded hover:bg-red-50 opacity-0 group-hover:opacity-100 transition">
                                  <Trash2 className="h-3.5 w-3.5 text-red-400" />
                                </button>
                                <div className="mb-3">
                                  <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Goal Statement</label>
                                  <Textarea value={goal.goal_text} onChange={e => updateGoal(areaIdx, goalIdx, 'goal_text', e.target.value)} placeholder="Measurable annual goal statement..." rows={2} className="rounded-xl border-[#E5E5E5] text-sm" />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                  <div>
                                    <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Baseline</label>
                                    <Input value={goal.baseline} onChange={e => updateGoal(areaIdx, goalIdx, 'baseline', e.target.value)} placeholder="Current performance" className="rounded-xl border-[#E5E5E5] text-sm h-9" />
                                  </div>
                                  <div>
                                    <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Target Date</label>
                                    <Input type="date" value={goal.target_date} onChange={e => updateGoal(areaIdx, goalIdx, 'target_date', e.target.value)} className="rounded-xl border-[#E5E5E5] text-sm h-9" />
                                  </div>
                                  <div>
                                    <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Measurement Method</label>
                                    <Input value={goal.measurement_method} onChange={e => updateGoal(areaIdx, goalIdx, 'measurement_method', e.target.value)} placeholder="How progress is measured" className="rounded-xl border-[#E5E5E5] text-sm h-9" />
                                  </div>
                                </div>
                              </div>

                              {/* Progress Reports Toggle */}
                              {periods.length > 0 && (
                                <div className="border-t border-[#E5E5E5]">
                                  <button className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[#F3F4F1]/50 transition-colors"
                                    onClick={() => setProgressOpen(prev => ({ ...prev, [goal.goal_id]: !prev[goal.goal_id] }))}
                                    data-testid={`progress-toggle-${areaIdx}-${goalIdx}`}>
                                    <div className="flex items-center gap-2">
                                      <BarChart3 className="h-3.5 w-3.5 text-[#3B6A9B]" />
                                      <span className="text-xs font-medium text-[#595959]">Progress Reports</span>
                                      {reportedCount > 0 && (
                                        <Badge className="bg-[#3B6A9B]/10 text-[#3B6A9B] text-[10px] px-1.5 py-0">
                                          {reportedCount}/{periods.length} reported
                                        </Badge>
                                      )}
                                    </div>
                                    {/* Quick status dots */}
                                    <div className="flex items-center gap-1">
                                      {reports.map(r => {
                                        const sc = getStatusConfig(r.status);
                                        const Icon = sc.icon;
                                        return <Icon key={r.period_number} className={`h-3 w-3 ${sc.color.split(' ')[0]}`} />;
                                      })}
                                      {isProgressOpen ? <ChevronUp className="h-3.5 w-3.5 text-[#595959] ml-2" /> : <ChevronDown className="h-3.5 w-3.5 text-[#595959] ml-2" />}
                                    </div>
                                  </button>

                                  {isProgressOpen && (
                                    <div className="px-4 pb-4 space-y-3" data-testid={`progress-panel-${areaIdx}-${goalIdx}`}>
                                      {reports.map(report => {
                                        const period = periods.find(p => p.period_number === report.period_number);
                                        const statusConf = getStatusConfig(report.status);

                                        return (
                                          <div key={report.period_number} className={`border rounded-xl p-3 ${statusConf.color.split(' ').slice(1).join(' ')}`}
                                            data-testid={`progress-report-${areaIdx}-${goalIdx}-${report.period_number}`}>
                                            <div className="flex items-center justify-between mb-2.5">
                                              <div className="flex items-center gap-2">
                                                <Calendar className="h-3 w-3 text-[#595959]" />
                                                <span className="text-xs font-semibold text-[#1C1C1C]">{period?.label || `Period ${report.period_number}`}</span>
                                                {period?.start_date && period?.end_date && (
                                                  <span className="text-[10px] text-[#595959]">{period.start_date} — {period.end_date}</span>
                                                )}
                                              </div>
                                              <Select value={report.status || 'not_reported'}
                                                onValueChange={v => updateProgressReport(areaIdx, goalIdx, report.period_number, 'status', v)}>
                                                <SelectTrigger className="w-[180px] rounded-lg text-xs h-7 bg-white" data-testid={`progress-status-${areaIdx}-${goalIdx}-${report.period_number}`}>
                                                  <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  {PROGRESS_STATUSES.map(s => (
                                                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-2">
                                              <div>
                                                <label className="text-[10px] font-medium text-[#595959] mb-0.5 block">Current Performance</label>
                                                <Input value={report.current_performance || ''} placeholder="e.g., 75% accuracy on 3 trials"
                                                  onChange={e => updateProgressReport(areaIdx, goalIdx, report.period_number, 'current_performance', e.target.value)}
                                                  className="rounded-lg border-[#E5E5E5] text-xs h-7 bg-white" data-testid={`progress-performance-${areaIdx}-${goalIdx}-${report.period_number}`} />
                                              </div>
                                              <div className="flex gap-2">
                                                <div className="flex-1">
                                                  <label className="text-[10px] font-medium text-[#595959] mb-0.5 block">Reported Date</label>
                                                  <Input type="date" value={report.reported_date || ''}
                                                    onChange={e => updateProgressReport(areaIdx, goalIdx, report.period_number, 'reported_date', e.target.value)}
                                                    className="rounded-lg border-[#E5E5E5] text-xs h-7 bg-white" />
                                                </div>
                                                <div className="flex-1">
                                                  <label className="text-[10px] font-medium text-[#595959] mb-0.5 block">Reported By</label>
                                                  <Input value={report.reported_by || ''} placeholder="Name"
                                                    onChange={e => updateProgressReport(areaIdx, goalIdx, report.period_number, 'reported_by', e.target.value)}
                                                    className="rounded-lg border-[#E5E5E5] text-xs h-7 bg-white" />
                                                </div>
                                              </div>
                                            </div>
                                            <div>
                                              <label className="text-[10px] font-medium text-[#595959] mb-0.5 block">Narrative</label>
                                              <Textarea value={report.narrative || ''} placeholder="Progress summary for this reporting period..."
                                                onChange={e => updateProgressReport(areaIdx, goalIdx, report.period_number, 'narrative', e.target.value)}
                                                rows={2} className="rounded-lg border-[#E5E5E5] text-xs bg-white" data-testid={`progress-narrative-${areaIdx}-${goalIdx}-${report.period_number}`} />
                                            </div>
                                            {report.data_source && report.data_source !== 'manual' && (
                                              <div className="mt-1.5">
                                                <Badge className="text-[10px] bg-purple-50 text-purple-600 border border-purple-200">
                                                  Source: {report.data_source}
                                                </Badge>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <Button variant="outline" size="sm" className="rounded-lg text-xs mt-3" onClick={() => addGoal(areaIdx)} data-testid={`add-goal-${areaIdx}`}>
                      <Plus className="h-3 w-3 mr-1" />Add Goal
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl" data-testid="goals-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Goals & Objectives'}
        </Button>
      </div>
    </div>
  );
}
